/**
 * Factory presets for ElementarySynthNode (Elementary Audio)
 *
 * Band-limited oscillators for clean, anti-aliased sound.
 */

import type { ElemOscillatorType } from './ElementarySynthNode';

export interface ElementarySynthPreset {
  name: string;
  description: string;
  params: {
    oscillator: ElemOscillatorType;
    unisonVoices?: number;
    unisonSpread?: number;
    attack: number;
    decay: number;
    sustain: number;
    release: number;
    detune?: number;
    volume?: number;
  };
}

export const ELEMENTARY_SYNTH_PRESETS: ElementarySynthPreset[] = [
  // ─────────────────────────────────────────────────────────────
  // Lead Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Clean Lead',
    description: 'Pristine band-limited sawtooth, smooth at any pitch',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 1,
      attack: 0.01,
      decay: 0.1,
      sustain: 0.7,
      release: 0.3
    }
  },
  {
    name: 'Super Saw',
    description: 'Rich unison sawtooth with wide spread',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 7,
      unisonSpread: 35,
      attack: 0.01,
      decay: 0.2,
      sustain: 0.6,
      release: 0.5
    }
  },
  {
    name: 'Silky Lead',
    description: 'Detuned square waves, warm without bite',
    params: {
      oscillator: 'blepsquare',
      unisonVoices: 3,
      unisonSpread: 12,
      attack: 0.02,
      decay: 0.15,
      sustain: 0.7,
      release: 0.4
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Bass Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Pure Sub',
    description: 'Clean sine wave sub bass, deep and powerful',
    params: {
      oscillator: 'sine',
      unisonVoices: 1,
      attack: 0.02,
      decay: 0.1,
      sustain: 0.9,
      release: 0.5
    }
  },
  {
    name: 'Reese Bass',
    description: 'Detuned saws for classic reese sound',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 2,
      unisonSpread: 15,
      attack: 0.01,
      decay: 0.4,
      sustain: 0.5,
      release: 0.3
    }
  },
  {
    name: 'Smooth Pluck Bass',
    description: 'Triangle bass with gentle attack',
    params: {
      oscillator: 'triangle',
      unisonVoices: 1,
      attack: 0.005,
      decay: 0.2,
      sustain: 0.15,
      release: 0.15
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Pad Sounds
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Velvet Pad',
    description: 'Slow attack with rich unison, silky ambient',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 5,
      unisonSpread: 20,
      attack: 1.0,
      decay: 0.5,
      sustain: 0.7,
      release: 2.5
    }
  },
  {
    name: 'Choir Pad',
    description: 'Wide spread square ensemble, organ-like',
    params: {
      oscillator: 'blepsquare',
      unisonVoices: 6,
      unisonSpread: 30,
      attack: 0.6,
      decay: 0.3,
      sustain: 0.75,
      release: 2.0
    }
  },
  {
    name: 'Celestial',
    description: 'Pure sine waves with subtle detuning',
    params: {
      oscillator: 'sine',
      unisonVoices: 4,
      unisonSpread: 6,
      attack: 1.5,
      decay: 0.2,
      sustain: 0.6,
      release: 4.0,
      detune: 2
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Pluck / Keys
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Crystal Pluck',
    description: 'Clean triangle pluck, bell-like clarity',
    params: {
      oscillator: 'triangle',
      unisonVoices: 1,
      attack: 0.001,
      decay: 0.4,
      sustain: 0.0,
      release: 0.6
    }
  },
  {
    name: 'Soft Keys',
    description: 'Gentle square wave keys, warm for chords',
    params: {
      oscillator: 'blepsquare',
      unisonVoices: 2,
      unisonSpread: 8,
      attack: 0.01,
      decay: 0.5,
      sustain: 0.3,
      release: 0.8
    }
  },
  {
    name: 'Clean Arp',
    description: 'Short blips for arpeggios, precise and clean',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 1,
      attack: 0.001,
      decay: 0.08,
      sustain: 0.0,
      release: 0.08
    }
  },

  // ─────────────────────────────────────────────────────────────
  // Special
  // ─────────────────────────────────────────────────────────────
  {
    name: 'Trance Lead',
    description: 'Classic trance supersaw, wide stereo spread',
    params: {
      oscillator: 'blepsaw',
      unisonVoices: 8,
      unisonSpread: 50,
      attack: 0.01,
      decay: 0.3,
      sustain: 0.5,
      release: 0.6
    }
  },
  {
    name: 'Analog Drift',
    description: 'Detuned triangle with slow drift, vintage character',
    params: {
      oscillator: 'triangle',
      unisonVoices: 3,
      unisonSpread: 25,
      detune: 8,
      attack: 0.1,
      decay: 0.4,
      sustain: 0.6,
      release: 1.0
    }
  }
];

export function getElementarySynthPreset(name: string): ElementarySynthPreset | undefined {
  return ELEMENTARY_SYNTH_PRESETS.find(p => p.name === name);
}

export function getElementarySynthPresetNames(): string[] {
  return ELEMENTARY_SYNTH_PRESETS.map(p => p.name);
}
